import {useMemo} from 'react';

import inferTypeFromLookupConfig from 'lookup-config/inferTypeFromLookupConfig';
import {LookupConfigDetailPaneProps} from 'lookup-config/LookupConfigDetailPane';

import {LookupStepInfo, LookupStepSpec, LookupStepType} from '../LookupConfigModalConstants';

/** These LookupStepTypes do not sort their contents on the backend */
const PRESERVE_ORDER_LOOKUP_TYPES = [LookupStepType.BOX_CONTAINERS, LookupStepType.WELLS];

/** Determines whether the Lookup Config Modal should show the option to set the sorting option of Lookups.
 *
 * We want to give users the option to sort their results alphabetically or retain original order if their
 * lookup returns multiple results.
 * This is only relevant for Notebook Lookup Columns as we don't generally allow customizing sorting for other
 * lookup usages.
 */
export function useShowFinalSortOption(
  props: Pick<
    LookupConfigDetailPaneProps,
    'detailSteps' | 'isMulti' | 'multiFlattenLevel' | 'previousLookupStepInfos' | 'lookupObjectSelected'
  > & {isNotebookLookup: boolean}
): boolean {
  const {
    detailSteps,
    isMulti,
    multiFlattenLevel,
    previousLookupStepInfos,
    lookupObjectSelected,
    isNotebookLookup,
  } = props;
  return useMemo(() => {
    if (!isNotebookLookup) {
      return false;
    }
    // Some LookupStepTypes force their own order on the backend
    // (see `LookupStep.has_order_specified` and `perform_notebook_lookups.py`).
    // If any step in the config is one of these, the "Sort multiple values alphabetically" toggle
    // is silently a no-op — hide it regardless of multiplexing.
    const anyPreviousStepPreservesOrder = previousLookupStepInfos.some((info) =>
      PRESERVE_ORDER_LOOKUP_TYPES.includes(info.lookupStep.type)
    );
    if (anyPreviousStepPreservesOrder) {
      return false;
    }
    // Show the sort option if the previous step is selected and the previous step is multiplex
    const previousStepIsMultiplex =
      lookupObjectSelected &&
      (isLookupMultiplex({detailStep: null, isMulti, multiFlattenLevel, previousLookupStepInfos}) ?? false);
    // Some LookupStepTypes do not sort their values on the backend, so don't show the option here if just
    // those steps are selected or the steps aren't multiplex.
    const anyDetailStepIsSortableAndMultiplex = detailSteps.some(
      (ds) =>
        !PRESERVE_ORDER_LOOKUP_TYPES.includes(ds.lookupStep.type) &&
        (isLookupMultiplex({detailStep: ds, isMulti, multiFlattenLevel, previousLookupStepInfos}) ?? false)
    );
    return previousStepIsMultiplex || anyDetailStepIsSortableAndMultiplex;
  }, [
    detailSteps,
    isMulti,
    multiFlattenLevel,
    previousLookupStepInfos,
    lookupObjectSelected,
    isNotebookLookup,
  ]);
}

/** Returns whether the current Lookup Config is multiplex.
 *
 * If it cannot be determined for some reason, returns undefined.
 */
function isLookupMultiplex(props: {
  detailStep: LookupStepInfo | null;
  isMulti: boolean;
  multiFlattenLevel?: number | null;
  previousLookupStepInfos: Array<LookupStepInfo>;
}): boolean | undefined {
  const {previousLookupStepInfos, detailStep, isMulti, multiFlattenLevel} = props;
  const startingSchema = previousLookupStepInfos.find(
    (lookupStepInfo) => lookupStepInfo.linkSchema != null
  )?.linkSchema;
  if (!startingSchema) {
    return undefined;
  }
  // collect the current selected lookup steps
  const lookupSteps: Array<LookupStepSpec> = [
    ...previousLookupStepInfos,
    ...(detailStep ? [detailStep] : []),
  ].reduce<Array<LookupStepSpec>>((collectedStepSpecs, lookupStepInfo) => {
    const lookupStepSpecs: Array<LookupStepSpec> = [];
    if (lookupStepInfo.lookupStep) {
      lookupStepSpecs.push(lookupStepInfo.lookupStep);
    }
    // add any collapsing lookup steps as they can affect multiplexing
    if (lookupStepInfo.collapsingLookupSteps) {
      lookupStepSpecs.push(...lookupStepInfo.collapsingLookupSteps.map((step) => step.lookupStep));
    }
    return collectedStepSpecs.concat(lookupStepSpecs);
  }, []);
  // determine if the final results of the lookup has multiple values
  const {isMultiplex} = inferTypeFromLookupConfig({isMulti, multiFlattenLevel, lookupSteps}, startingSchema);
  return isMultiplex;
}
